/*
  Dispatch countdown.

  On countdown days (weekdays by default) it counts down to the daily cutoff in
  Melbourne time (or fixed AEST). From the cutoff until midnight, and all day on
  other days, it shows the fallback text with no timer. It re-checks every
  second, so it switches back at midnight without a reload.

  It also keeps this row and the shipping notice below it the same width, so the
  mobile CSS can centre them as one aligned pair.

  The clock is the shopper's device clock converted to the store's time zone,
  so a device set to the wrong time will show the wrong countdown.
*/
if (!customElements.get('dispatch-timer')) {
  var DISPATCH_WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  customElements.define(
    'dispatch-timer',
    class DispatchTimer extends HTMLElement {
      connectedCallback() {
        var hour = parseInt(this.dataset.cutoffHour, 10);
        this.cutoffSeconds = (isNaN(hour) ? 17 : hour) * 3600;
        this.timeZone = this.dataset.timezone || 'Australia/Melbourne';
        this.textBefore = this.dataset.textBefore || '';
        this.fallback = this.dataset.textAfter || 'same day dispatch';
        this.days = (this.dataset.countdownDays || '1,2,3,4,5')
          .split(',')
          .map(function (n) {
            return parseInt(n, 10);
          })
          .filter(function (n) {
            return !isNaN(n);
          });
        this.textEl = this.querySelector('[data-dispatch-text]');
        if (!this.textEl || this.textBefore.indexOf('[timer]') === -1) return;

        this.mode = null;
        this.tick();
        this.hidden = false;
        this.syncPair();
        this.interval = setInterval(this.tick.bind(this), 1000);

        var self = this;
        this.onResize = function () {
          cancelAnimationFrame(self.resizeFrame);
          self.resizeFrame = requestAnimationFrame(self.syncPair.bind(self));
        };
        window.addEventListener('resize', this.onResize);
        // Web fonts change text widths once they load.
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(this.syncPair.bind(this));
        }
      }

      disconnectedCallback() {
        clearInterval(this.interval);
        if (this.onResize) window.removeEventListener('resize', this.onResize);
        cancelAnimationFrame(this.resizeFrame);
      }

      /* Weekday (0 = Sunday) and seconds since midnight in the store's time zone. */
      clock(now) {
        if (this.timeZone !== 'fixed-aest') {
          try {
            var parts = new Intl.DateTimeFormat('en-GB', {
              timeZone: this.timeZone,
              weekday: 'short',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hourCycle: 'h23'
            }).formatToParts(now);
            var get = function (type) {
              var part = parts.filter(function (p) {
                return p.type === type;
              })[0];
              return part ? part.value : '';
            };
            var day = DISPATCH_WEEKDAYS[get('weekday')];
            if (day !== undefined) {
              return {
                day: day,
                seconds:
                  (parseInt(get('hour'), 10) % 24) * 3600 +
                  parseInt(get('minute'), 10) * 60 +
                  parseInt(get('second'), 10)
              };
            }
          } catch (e) {
            // Fall through to fixed AEST if the time zone is not supported.
          }
        }
        var shifted = new Date(now.getTime() + 10 * 3600 * 1000);
        return {
          day: shifted.getUTCDay(),
          seconds: shifted.getUTCHours() * 3600 + shifted.getUTCMinutes() * 60 + shifted.getUTCSeconds()
        };
      }

      tick() {
        var clock = this.clock(new Date());
        var remaining = this.cutoffSeconds - clock.seconds;
        var counting = this.days.indexOf(clock.day) !== -1 && remaining > 0;
        var mode = counting ? 'countdown' : 'fallback';

        if (mode !== this.mode) {
          if (counting) {
            this.renderCountdown();
          } else {
            this.renderFallback();
          }
          this.mode = mode;
          // The row's text changed length, so the pair needs re-measuring.
          if (!this.hidden) this.syncPair();
        }
        if (counting) this.timeEl.textContent = this.format(remaining);
      }

      /* Builds "text [timer] text" with the countdown in its own element. */
      renderCountdown() {
        var pieces = this.textBefore.split('[timer]');
        this.textEl.textContent = '';
        this.timeEl = document.createElement('strong');
        this.timeEl.className = 'dispatch-timer__time';
        this.textEl.appendChild(document.createTextNode(pieces[0]));
        this.textEl.appendChild(this.timeEl);
        this.textEl.appendChild(document.createTextNode(pieces.slice(1).join('')));
      }

      renderFallback() {
        this.textEl.textContent = this.fallback;
        this.timeEl = null;
      }

      /*
        Gives this row and the shipping notice after it the same width - the wider
        of the two - so the mobile CSS can centre them as one aligned pair.
      */
      syncPair() {
        if (this.hidden || !this.closest) return;
        var block = this.closest('.product__dispatch-timer');
        var shipping = null;
        for (var el = block && block.nextElementSibling; el; el = el.nextElementSibling) {
          if (el.classList.contains('product__shipping-notice')) {
            shipping = el;
            break;
          }
        }
        if (!shipping) return;

        // Measure the countdown at its longest time so the pair never shifts as it ticks.
        var widest = Math.max(
          this.naturalWidth(this, this.mode === 'countdown' ? this.format(this.cutoffSeconds) : null),
          this.naturalWidth(shipping, null)
        );
        var value = Math.ceil(widest) + 'px';
        this.style.setProperty('--dispatch-pair-width', value);
        shipping.style.setProperty('--dispatch-pair-width', value);
      }

      /* Single-line width of a row's content, optionally with a stand-in countdown. */
      naturalWidth(row, timeText) {
        var saved = null;
        if (timeText !== null && this.timeEl) {
          saved = this.timeEl.textContent;
          this.timeEl.textContent = timeText;
        }
        var previous = row.style.width;
        row.style.width = 'max-content';
        var width = row.getBoundingClientRect().width;
        row.style.width = previous;
        if (saved !== null) this.timeEl.textContent = saved;
        return width;
      }

      format(total) {
        var h = Math.floor(total / 3600);
        var m = Math.floor((total % 3600) / 60);
        var s = total % 60;
        var pad = function (n) {
          return (n < 10 ? '0' : '') + n;
        };
        return h > 0 ? h + 'h ' + pad(m) + 'm ' + pad(s) + 's' : m + 'm ' + pad(s) + 's';
      }
    }
  );
}
